import { useState } from "react";
import {
  LuCircleHelp,
  LuLoaderCircle,
  LuPencil,
  LuRefreshCw,
  LuTrash2,
} from "react-icons/lu";
import { HttpAssistantIsWorkingException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { ImageService } from "../../../services/imageService";
import { ProjectChatUserMessage } from "../../../view_stores/chat_messages/chatModels";
import {
  useChatElements,
  useChatMessagesActions,
} from "../../../view_stores/chat_messages/useChatMessagesStore";
import { usePromptInputActions } from "../../../view_stores/usePromptInputStore";
import { useTechnicalErrorHandlerActions } from "../../../view_stores/useTechnicalErrorHandlerStore";

// Helper function to convert attachment to File
async function attachmentToFile(attachment: {
  url?: string;
  content?: string;
  filename: string;
  mimeType: string;
}): Promise<File | null> {
  // Check if attachment has URL (new format) or content (legacy format)
  if (attachment.url) {
    // Fetch image from URL and convert to File
    return await ImageService.fetchImageAsFile(
      attachment.url,
      attachment.filename,
      attachment.mimeType
    );
  } else if (attachment.content) {
    // Use legacy base64 content
    return ImageService.base64ToFile(
      attachment.content,
      attachment.filename,
      attachment.mimeType
    );
  }
  return null;
}

export default function ProjectTechnicalErrorActions({
  onEdit,
}: {
  onEdit: () => void;
}) {
  const chatElements = useChatElements();

  const canDecline = chatElements.filter((el) => el instanceof ProjectChatUserMessage).length > 1;

  return (
    <div
      className="h-full w-full overflow-hidden
    flex flex-col items-center justify-start"
    >
      <HelpTooltip canDecline={canDecline} />

      <Actions canDecline={canDecline} onEdit={onEdit} />
    </div>
  );
}

function HelpTooltip({ canDecline }: { canDecline: boolean }) {
  const tooltipContent = () => {
    let content = `The last attempted change could not be applied due to a technical issue, and the project may not function correctly. Please choose how you'd like to proceed:
          <ul style="list-style-type: disc; padding-left: 12px;">
            <li>Retry: Attempt the same change again.</li>
            <li>Edit: Modify your request and try again.</li>`;

    if (canDecline) {
      content += `<li>Discard: Cancel the last change and revert to the previous version of the project.</li>`;
    }
    content += `</ul>`;
    return content;
  };

  return (
    <div className="w-full flex justify-end max-w-[600px] pt-[8px] pb-[12px]">
      <span data-tooltip-id="chat-tooltip" data-tooltip-html={tooltipContent()}>
        <LuCircleHelp
          className="text-light-placeholder dark:text-dark-placeholder
            cursor-pointer"
          size={20}
        />
      </span>
    </div>
  );
}

function Actions({
  canDecline,
  onEdit,
}: {
  canDecline: boolean;
  onEdit: () => void;
}) {
  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(
    null
  );
  const chatElements = useChatElements();

  const { rollback } = useTechnicalErrorHandlerActions();
  const { setPrompt, setAttachedImage } = usePromptInputActions();
  const { removeTemporaryMessages } = useChatMessagesActions();
  const { showSnackbar } = useSnackbar();

  const handleRetry = async () => {
    setSelectedActionIndex(0);
    const latestUserMessage = [...chatElements]
      .reverse()
      .find(
        (el) => el instanceof ProjectChatUserMessage
      ) as ProjectChatUserMessage;

    if (latestUserMessage) {
      let file: File | null = null;
      if (
        latestUserMessage.value.attachments &&
        latestUserMessage.value.attachments.length > 0
      ) {
        file = await attachmentToFile(latestUserMessage.value.attachments[0]);
      }
      try {
        await rollback(latestUserMessage.value.message, file);
      } catch (error) {
        if (error instanceof HttpAssistantIsWorkingException) {
          removeTemporaryMessages();
          showSnackbar(
            "Previous action is still in progress, try again later.",
            {
              type: "error",
            }
          );
        }
      }
    }
  };

  const handleEdit = async () => {
    setSelectedActionIndex(1);
    onEdit();
    const latestUserMessage = [...chatElements]
      .reverse()
      .find(
        (el) => el instanceof ProjectChatUserMessage
      ) as ProjectChatUserMessage;

    if (latestUserMessage) {
      setPrompt(latestUserMessage.value.message);
      if (
        latestUserMessage.value.attachments &&
        latestUserMessage.value.attachments.length > 0
      ) {
        const file = await attachmentToFile(
          latestUserMessage.value.attachments[0]
        );
        setAttachedImage(file);
      }
    }
  };

  const handleDiscard = () => {
    setSelectedActionIndex(2);
    rollback(null, null);
  };

  return (
    <div className="w-full flex gap-4 max-w-[500px]">
      <ActionBtn
        id="project-technical-error-edit"
        label="Edit"
        icon={<LuPencil />}
        isDisabled={selectedActionIndex !== null}
        isLoading={selectedActionIndex === 1}
        bgColor="bg-white dark:bg-[#272829]"
        onClick={handleEdit}
      />

      {canDecline && (
        <ActionBtn
          id="project-technical-error-discard"
          label="Discard"
          icon={<LuTrash2 />}
          isDisabled={selectedActionIndex !== null}
          isLoading={selectedActionIndex === 2}
          bgColor="bg-white dark:bg-[#272829]"
          onClick={handleDiscard}
        />
      )}

      <ActionBtn
        id="project-technical-error-retry"
        label="Retry"
        icon={<LuRefreshCw />}
        isDisabled={selectedActionIndex !== null}
        isLoading={selectedActionIndex === 0}
        bgColor="bg-[#16A34A] text-white"
        onClick={handleRetry}
      />
    </div>
  );
}

function ActionBtn({
  id,
  label,
  icon,
  isDisabled,
  isLoading,
  bgColor,
  onClick,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  isDisabled: boolean;
  isLoading: boolean;
  bgColor: string;
  onClick: () => void;
}) {
  return (
    <div
      className="w-full rounded-[4px] 
    relative overflow-hidden"
    >
      <button
        id={id}
        onClick={onClick}
        className={`w-full flex items-center justify-center 
    gap-2 py-2
    hover:brightness-125 hover:cursor-pointer
    transition-all duration-300 ${bgColor}`}
        disabled={isDisabled}
      >
        {icon}
        {label}
      </button>
      {isLoading && (
        <div
          className="absolute top-0 bg-black/50 w-full h-full
          flex items-center justify-center"
        >
          <LuLoaderCircle className="animate-spin text-white" size={24} />
        </div>
      )}
    </div>
  );
}
